/**
 * Better Auth client factory.
 *
 * This is the seam that makes the auth layer reusable across platforms: web and
 * Expo share every call-site (`signIn.email`, `signUp.email`, `useSession`, …)
 * and differ only in how the session is transported —
 *   web    → httpOnly cookie, sent automatically, same-origin
 *   mobile → bearer token from SecureStore (Better Auth expo plugin)
 *
 * Web is implemented now; the mobile branch is intentionally a documented stub
 * so nothing about today's build blocks it later.
 */
import { createAuthClient } from 'better-auth/react';

export interface WebAuthClientOptions {
  /** Same-origin in production, so this is normally left empty. */
  baseURL?: string;
}

/**
 * Web client — session rides in an httpOnly cookie the browser sends
 * automatically. No token is ever exposed to JavaScript (SECURITY.md §3).
 */
export function createWebAuthClient(options: WebAuthClientOptions = {}) {
  return createAuthClient({
    baseURL: options.baseURL ?? '',
  });
}

export type AuthClient = ReturnType<typeof createWebAuthClient>;

/**
 * Mobile (Expo) — add when apps/mobile lands:
 *
 *   import { expoClient } from '@better-auth/expo/client'
 *   import * as SecureStore from 'expo-secure-store'
 *
 *   createAuthClient({
 *     baseURL: WORKER_URL,
 *     plugins: [expoClient({ scheme: 'ideabrief', storage: SecureStore })],
 *   })
 *
 * The Worker already enables the bearer plugin, so no server change is needed.
 */
